/**
 * Помощник: ключи, что он спрашивал и что вообще умеет.
 *
 * Своя вкладка, а не угол на странице ключей. Помощник — это отдельное
 * хозяйство: у него свои ключи, свой журнал и свой набор инструментов, и
 * вопросы к нему задают отдельные — «кому выдан доступ», «что он сегодня
 * спрашивал», «а восстановить он может?». Ответы на них лежали в трёх разных
 * местах, из которых два были в исходниках.
 */
import { useState } from 'react';
import { useQuery } from '../transport/hooks.ts';
import { Segmented } from '../components/Segmented.tsx';
import { useParam } from '../router.tsx';
import { McpKeys } from '../components/McpKeys.tsx';

interface CallRow {
  at: string;
  kid: string;
  label: string;
  tool: string;
  method: string | null;
  effect: 'read' | 'write' | 'dangerous' | 'unknown';
  ok: boolean;
  ms: number;
  args: Record<string, unknown> | null;
  error: string | null;
}

interface ToolRow {
  tool: string;
  method: string;
  summary: string;
  detail: string;
  effect: 'read' | 'write' | 'dangerous';
  example: Record<string, unknown> | null;
  fields: { name: string; type: string; required: boolean }[];
}

const EFFECT_WORD: Record<string, string> = {
  read: 'читает',
  write: 'меняет',
  dangerous: 'опасное',
  unknown: 'неизвестный',
};

/**
 * Журнал вызовов.
 *
 * Все, включая читающие. Владелец, выдавший ключ, вправе знать, что помощник
 * спрашивал: «сколько раз он смотрел снимки прода» — вопрос про доверие, а не
 * про статистику. Меняющие при этом видно сразу: они помечены и их можно
 * оставить одни.
 */
function Calls(): React.JSX.Element {
  const [changingOnly, setChangingOnly] = useState(false);
  const { data, loading, reload } = useQuery<{ items: CallRow[] }>(
    'mcp.calls', { limit: 150, changingOnly },
  );

  if (loading && !data) return <p className="muted">Загружаю журнал…</p>;
  const rows = data?.items ?? [];

  return (
    <>
      <div className="mcp__row">
        <Segmented
          ariaLabel="что показывать"
          active={changingOnly ? 'changing' : 'all'}
          onSelect={(id) => setChangingOnly(id === 'changing')}
          size="sm"
          items={[
            { id: 'all', label: 'Все вызовы' },
            { id: 'changing', label: 'Только меняющие' },
          ]}
        />
        <button type="button" className="button button--ghost button--small" onClick={reload}>
          Обновить
        </button>
      </div>

      {rows.length === 0 ? (
        <p className="muted">
          {changingOnly
            ? 'Помощник ничего не менял.'
            : 'Вызовов пока не было. Журнал появится, как только помощник спросит.'}
        </p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Когда</th><th>Кто</th><th>Инструмент</th><th>Что делал</th>
              <th>Ушло</th><th>Итог</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.at}-${i}`} className={r.ok ? undefined : 'row--warn'}>
                <td className="mono">{new Date(r.at).toLocaleString('ru-RU')}</td>
                <td>{r.label}</td>
                <td className="mono">{r.tool}</td>
                <td>
                  <span className={`calls__effect calls__effect--${r.effect}`}>
                    {EFFECT_WORD[r.effect]}
                  </span>
                  {r.args && Object.keys(r.args).length > 0 && (
                    <code className="mono muted calls__args" title={JSON.stringify(r.args)}>
                      {JSON.stringify(r.args).slice(0, 60)}
                    </code>
                  )}
                </td>
                <td className="mono">{r.ms} мс</td>
                <td>{r.ok ? <span className="muted">ок</span> : (r.error ?? 'отказ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

/**
 * Справочник инструментов — из кода, а не из отдельного текста.
 *
 * Собирается в момент запроса из того же реестра, из которого сделаны сами
 * инструменты. Документ, написанный руками или собранный при релизе, расходится
 * с кодом на первой же правке между релизами; тут расходиться нечему.
 *
 * Раздел «чего помощник не может» — не для полноты. Он отвечает на вопрос до
 * того, как его зададут, и показывает, что это решение, а не недоделка.
 */
function Tools(): React.JSX.Element {
  const { data, loading } = useQuery<{
    exposed: ToolRow[];
    hidden: { method: string; summary: string; why: string }[];
  }>('mcp.tools');

  if (loading && !data) return <p className="muted">Загружаю…</p>;
  const exposed = data?.exposed ?? [];
  const hidden = data?.hidden ?? [];

  return (
    <div className="tools">
      <p className="muted">
        Отдано <b>{exposed.length}</b> инструментов. Описание собрано из кода в
        момент запроса — разойтись с тем, что работает, оно не может.
      </p>

      {exposed.map((t) => (
        <section key={t.tool} className="tools__item">
          <h4 className="tools__name">
            <code className="mono">{t.tool}</code>
            <span className={`calls__effect calls__effect--${t.effect}`}>
              {EFFECT_WORD[t.effect]}
            </span>
          </h4>
          <p className="tools__summary">{t.summary}</p>
          <p className="muted tools__detail">{t.detail}</p>

          {t.fields.length > 0 && (
            <ul className="tools__fields">
              {t.fields.map((f) => (
                <li key={f.name}>
                  <code className="mono">{f.name}</code>
                  <span className="muted"> {f.type}</span>
                  {f.required && <span className="tools__req"> обязательно</span>}
                </li>
              ))}
            </ul>
          )}
          {t.example && (
            <pre className="mono tools__example">{JSON.stringify(t.example)}</pre>
          )}
        </section>
      ))}

      <h3 className="tools__title">Чего помощник не может — и почему</h3>
      <table className="table">
        <thead><tr><th>Метод</th><th>Что это</th><th>Почему не отдан</th></tr></thead>
        <tbody>
          {hidden.map((h) => (
            <tr key={h.method}>
              <td className="mono">{h.method}</td>
              <td>{h.summary}</td>
              <td className="muted">{h.why}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Mcp(): React.JSX.Element {
  const [tab, setTab] = useParam('section', 'keys', ['keys', 'calls', 'tools'] as const);

  return (
    <section className="panel panel--fill">
      <header className="panel__head">
        <h2 className="panel__title">Помощник (MCP)</h2>
      </header>

      <div className="panel__tabs">
        <Segmented
          ariaLabel="разделы помощника"
          active={tab}
          onSelect={(id) => setTab(id as typeof tab)}
          items={[
            { id: 'keys', label: 'Ключи и подключение' },
            { id: 'calls', label: 'История запросов' },
            { id: 'tools', label: 'Что он умеет' },
          ]}
        />
      </div>

      <div className="panel__scroll">
        {tab === 'keys' && <McpKeys />}
        {tab === 'calls' && <Calls />}
        {tab === 'tools' && <Tools />}
      </div>
    </section>
  );
}
